import { create } from 'zustand';
import api from '../utils/api';

const useSubscriptionStore = create((set, get) => ({
  subscription: null,
  plans: [],
  usageHistory: [],
  paymentHistory: [],
  loading: false,
  error: null,

  // Получить все планы
  fetchPlans: async () => {
    try {
      set({ loading: true, error: null });
      const response = await api.get('/api/subscriptions/plans');
      set({ plans: response.data.data, loading: false });
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch plans', loading: false });
    }
  },

  // Получить текущую подписку
  fetchSubscription: async () => {
    try {
      set({ loading: true, error: null });
      const response = await api.get('/api/subscriptions/current');
      set({ subscription: response.data.data, loading: false });
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch subscription', loading: false });
    }
  },

  // Изменить план через оплату YooKassa
  changePlan: async (plan, promoCode = null, billingPeriod = 'monthly') => {
    try {
      set({ loading: true, error: null });

      if (plan === 'free') {
        const response = await api.post('/api/subscriptions/change-plan', { plan });
        set({ subscription: response.data.data, loading: false });
        return { success: true };
      }

      const response = await api.post('/api/payments/create', {
        planId: plan,
        promoCode: promoCode || undefined,
        billingPeriod
      }, { timeout: 90000 });
      set({ loading: false });

      // Получили confirmation URL - редиректим на YooKassa
      if (response.data.success && response.data.data.confirmationUrl) {
        // Сохраняем paymentId для проверки после возврата
        localStorage.setItem('lastPaymentId', response.data.data.paymentId);
        window.location.href = response.data.data.confirmationUrl;
        return { success: true, redirecting: true };
      }

      return { success: false, error: 'Не удалось создать платеж' };
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to change plan';
      set({ error: errorMsg, loading: false });
      return { success: false, error: errorMsg };
    }
  },

  // Отменить подписку
  cancelSubscription: async () => {
    try {
      set({ loading: true, error: null });
      const response = await api.post('/api/subscriptions/cancel');
      set({ subscription: response.data.data, loading: false });
      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to cancel subscription';
      set({ error: errorMsg, loading: false });
      return { success: false, error: errorMsg };
    }
  },

  // Получить статистику использования
  fetchUsageStats: async () => {
    try {
      set({ loading: true, error: null });
      const response = await api.get('/api/subscriptions/usage');
      set({ subscription: response.data.data.subscription, loading: false });
      return response.data.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch usage stats', loading: false });
    }
  },

  // Получить историю использования
  fetchUsageHistory: async (params = {}) => {
    try {
      set({ loading: true, error: null });
      const response = await api.get('/api/subscriptions/usage/history', { params });
      set({ usageHistory: response.data.data, loading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch usage history', loading: false });
    }
  },

  // Получить историю платежей
  fetchPaymentHistory: async () => {
    try {
      set({ loading: true, error: null });
      const response = await api.get('/api/payments/history');
      // getPaymentHistory возвращает {payments: [...], pagination: {...}}
      const payments = response.data.data?.payments || [];
      set({ paymentHistory: payments, loading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch payment history', loading: false });
      set({ paymentHistory: [], loading: false });
    }
  },

  // Отменить автопродление
  cancelAutoRenewal: async () => {
    try {
      set({ loading: true, error: null });
      const response = await api.post('/api/payments/cancel-auto-renewal');
      set({ subscription: response.data.data, loading: false });
      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to cancel auto-renewal';
      set({ error: errorMsg, loading: false });
      return { success: false, error: errorMsg };
    }
  },

  // Проверить есть ли лимит
  hasLimit: (type, amount = 1) => {
    const { subscription } = get();
    if (!subscription) return false;

    if (type === 'video') {
      return subscription.videoUsed + amount <= subscription.videoLimit;
    } else if (type === 'ai') {
      // Business план - безлимит AI
      if (subscription.plan === 'business') return true;
      return subscription.aiUsed + amount <= subscription.aiLimit;
    }

    return false;
  },

  // Получить процент использования
  getUsagePercent: (type) => {
    const { subscription } = get();
    if (!subscription) return 0;

    if (type === 'video') {
      if (subscription.videoLimit === 0) return 0;
      return Math.round((subscription.videoUsed / subscription.videoLimit) * 100);
    } else if (type === 'ai') {
      if (subscription.plan === 'business') return 0; // безлимит
      if (subscription.aiLimit === 0) return 0;
      return Math.round((subscription.aiUsed / subscription.aiLimit) * 100);
    }

    return 0;
  },

  // Сбросить ошибку
  clearError: () => set({ error: null })
}));

export { useSubscriptionStore };
export default useSubscriptionStore;
